// React and React DOM imports
import { useCallback, useEffect, useRef, useState } from 'react';

// OpenLayers imports
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import ImageLayer from 'ol/layer/Image';
import Static from 'ol/source/ImageStatic';
import { Select } from 'ol/interaction';
import { click } from 'ol/events/condition';
import { boundingExtent, getTopRight } from 'ol/extent';
import { Polygon } from 'ol/geom';
import { unByKey } from 'ol/Observable';
import { Fill, Stroke, Style, Text } from 'ol/style';

// Utility functions
import {
	convertToRgba,
	imagePositionsToMapCoordinates,
	preventClickOnOverlay,
} from '@utils/map/helpers';
import { createTooltip } from '@utils/map/tooltip.overlay';
import { fetchImageUrls } from '@utils/map/layer/singleImage';

// Component imports
import { SelectedImageOverlayTooltip } from './OverlayTooltips';
import MapLoader from '@routes/userRoutes/projects/singleProject/components/MapLoader';

// Context imports
import { useProject } from '@contexts/Project.context';

const setPolygonStyle = (feature, colorScheme) => {
	const color = convertToRgba(colorScheme[feature.get('classId')]?.color);
	feature.setStyle(
		new Style({
			stroke: new Stroke({
				color: color,
				width: 3,
			}),
			fill: new Fill({
				color: 'rgba(255, 255, 255, 0.2)',
			}),
			text: new Text({
				text: `${(feature.get('confidence') * 100).toFixed(1)}%`,
				font: '14px Calibri,sans-serif',
				fill: new Fill({
					color: '#ffffff',
				}),
				stroke: new Stroke({
					color: '#000000',
					width: 3,
				}),
				placement: 'line',
				textAlign: 'left',
				textBaseline: 'bottom',
				overflow: true,
			}),
		})
	);
};

const setAnnotationPolygonStyle = (feature, colorScheme) => {
	const classId = feature.get('classId');
	const color = convertToRgba(colorScheme[classId]?.color) || 'rgba(255, 165, 0, 1)';

	feature.setStyle(
		new Style({
			stroke: new Stroke({
				color: color,
				width: 2,
				lineDash: [6, 4],
			}),
			fill: new Fill({
				color: 'rgba(255, 255, 255, 0.1)',
			}),
		})
	);
};

/**
 * Renders GeoJSON polygon features onto an OL VectorLayer with coordinate conversion and styling.
 * @param {Object} params
 * @param {Object|null} params.data - Object with `features` array (e.g. { features: [...], classes: [...] }) or null
 * @param {import('ol/layer/Vector').default} params.layer - Target OL VectorLayer
 * @param {Array} params.extent - Image extent [minX, minY, maxX, maxY]
 * @param {Function} params.styleFn - Style function (feature, colorScheme) => void
 * @param {Object} params.colorScheme - Color scheme object
 */
const renderPolygonsOnLayer = ({ data, layer, extent, styleFn, colorScheme }) => {
	const source = layer.getSource();
	source.clear();

	if (!data?.features?.length) return;

	data.features.forEach(item => {
		const mapCoords = imagePositionsToMapCoordinates(item.geometry.coordinates, extent);
		const polygon = new Polygon(mapCoords);
		const polygonFeature = new Feature({
			geometry: polygon,
			data: item.properties,
			classId: item.properties.classid,
			className: item.properties.classname,
			confidence: item.properties.confidence,
		});
		styleFn(polygonFeature, colorScheme);
		source.addFeature(polygonFeature);
	});
};

const SelectedSingleImageLayer = ({ singleImageLayer, singleImageLayerTooltipRef }) => {
	const {
		colorScheme,
		mapObject,
		defaultProjection,
		project,
		selectedSingleImage,
		toolBarVisible,
		interactionsDisabled,
		dispatch,
	} = useProject();

	const interactionsDisabledRef = useRef(interactionsDisabled);
	interactionsDisabledRef.current = interactionsDisabled;

	const selectInteractionKey = useRef(null);
	const selectedActive = useRef(false);

	const imageLayer = useRef(null);
	const polygonLayer = useRef(null);
	const annotationPolygonLayer = useRef(null);

	const overlay = useRef(null);
	const overlayRef = useRef(null);

	const imageAbortControllerRef = useRef(null);

	const [loading, setLoading] = useState(false);
	const [overlayContent, setOverlayContent] = useState(null);

	// Create image loader with abort support
	const createImageLoader = signal => (image, src) => {
		const img = image.getImage();

		const abortHandler = () => (img.src = '');

		if (signal.aborted) return abortHandler();

		signal.addEventListener('abort', abortHandler, { once: true });

		const loadHandler = () => signal.removeEventListener('abort', abortHandler);
		const errorHandler = () => signal.removeEventListener('abort', abortHandler);

		img.addEventListener('load', loadHandler, { once: true });
		img.addEventListener('error', errorHandler, { once: true });

		img.src = src;
	};

	// Function to load full resolution image seamlessly in the background
	const loadFullResImage = (fullResImageUrl, extent) => {
		// Reuse the same abort controller (will be created fresh on next image change)
		const fullResImageSignal = imageAbortControllerRef.current.signal;

		// Create a new source for full resolution image
		const fullResImageSource = new Static({
			url: fullResImageUrl,
			visible: true,
			projection: defaultProjection,
			imageExtent: extent,
			imageLoadFunction: createImageLoader(fullResImageSignal),
		});
		imageLayer.current?.setSource(fullResImageSource);

		const handleFullResImageLoad = event => {
			// Check if request was aborted
			if (fullResImageSignal.aborted) return;

			const { type } = event;

			if (type === 'imageloaderror') console.error('Full resolution image load error');

			// Cleanup listeners regardless of success or error
			fullResImageSource.un('imageloadend', handleFullResImageLoad);
			fullResImageSource.un('imageloaderror', handleFullResImageLoad);
		};

		// Attach event listeners to the full resolution image source
		fullResImageSource.on('imageloadend', handleFullResImageLoad);
		fullResImageSource.on('imageloaderror', handleFullResImageLoad);
	};

	const updateImage = async selectedFeature => {
		// Cancel any ongoing image load requests
		if (imageAbortControllerRef.current) {
			imageAbortControllerRef.current.abort();
			imageAbortControllerRef.current = null;
		}

		// Create new AbortController for this request
		imageAbortControllerRef.current = new AbortController();
		const signal = imageAbortControllerRef.current.signal;

		// Hide the image layer initially
		imageLayer.current?.setOpacity(0);

		// Hide the single image layer tooltip
		singleImageLayerTooltipRef?.setPosition(undefined);

		// Clear any existing detections
		polygonLayer.current?.setVisible(false);
		polygonLayer.current?.getSource()?.clear();
		annotationPolygonLayer.current?.setVisible(false);
		annotationPolygonLayer.current?.getSource()?.clear();

		// Hide overlay
		overlay.current?.setPosition(undefined);

		// There was a click outside of an image, reset and return
		if (!selectedFeature && selectedActive.current) {
			// Hide the image layer
			imageLayer.current.setVisible(false);
			// Show the single photos layer
			singleImageLayer.setVisible(true);
			// Mark that no feature is selected
			selectedActive.current = false;

			// Show the toolbar
			dispatch({ type: 'setToolBarVisible', payload: true });

			// Stop loading if it was in progress
			if (loading) setLoading(false);

			if (overlayContent) setOverlayContent(null);

			return;
		}

		// Get properties from the selected feature
		const imageValues = selectedFeature?.getProperties();
		if (!imageValues) return;

		// Set loading state
		setLoading(true);

		// Lazily fetch compressed and full-res URLs if not already cached on the feature
		let { image: compressedImageUrl, fullResImage: fullResImageUrl } = imageValues;
		if (!compressedImageUrl || !fullResImageUrl) {
			try {
				const { compressedUrl, fullResUrl } = await fetchImageUrls(
					project.uuid,
					imageValues.name,
					signal
				);
				compressedImageUrl = compressedUrl ?? compressedImageUrl;
				fullResImageUrl = fullResUrl ?? fullResImageUrl;

				// Cache on the feature so subsequent clicks don't re-fetch
				selectedFeature.set('image', compressedImageUrl);
				selectedFeature.set('fullResImage', fullResImageUrl);
			} catch (error) {
				if (signal.aborted) return;
				console.error('Error fetching image URLs:', error);
				setLoading(false);
				return;
			}
		}

		// If still no URLs after fetch, return
		if (!compressedImageUrl && !fullResImageUrl) {
			setLoading(false);
			return;
		}

		// Hide the toolbar if it is visible
		if (toolBarVisible) {
			dispatch({ type: 'setToolBarVisible', payload: false });
		}

		// Mark that a feature is selected
		selectedActive.current = true;

		// Hide the single photos layer
		singleImageLayer.setVisible(false);

		// Get the extent of the geometry
		const polygonCoordinates = imageValues.polygons;
		const selectedExtent = boundingExtent(polygonCoordinates);

		// Top, right, bottom, left
		const padding = [75, 0, 210, 0];

		// Zoom to the selected image
		const view = mapObject.getView();
		view.fit(selectedExtent, { padding });

		// Create the source for the image layer
		const compressedImageSource = new Static({
			url: compressedImageUrl,
			visible: true,
			projection: defaultProjection,
			imageExtent: selectedExtent,
			imageLoadFunction: createImageLoader(signal),
		});
		imageLayer.current.setSource(compressedImageSource);

		// Add HTML content to overlay
		const { name, center, rotation } = imageValues;
		setOverlayContent({
			name,
			center,
			rotation,
		});

		// Check if the selected image has detections
		const detections = imageValues.detections;
		renderPolygonsOnLayer({
			data: detections,
			layer: polygonLayer.current,
			extent: selectedExtent,
			styleFn: setPolygonStyle,
			colorScheme,
		});

		// Check if the selected image has annotations (training data)
		const annotations = imageValues.annotations;
		renderPolygonsOnLayer({
			data: annotations,
			layer: annotationPolygonLayer.current,
			extent: selectedExtent,
			styleFn: setAnnotationPolygonStyle,
			colorScheme,
		});

		// Function to handle compressed image load events
		const handleCompressedImageLoad = event => {
			// Check if request was aborted
			if (signal.aborted) return;

			const { type } = event;

			if (type === 'imageloadend') {
				// Set the image layer opacity to fully visible
				imageLayer?.current?.setOpacity(1);
				if (detections?.features?.length > 0) {
					// Make the polygon layer visible if there are detections
					polygonLayer?.current?.setVisible(true);
				}
				if (annotations?.features?.length > 0) {
					annotationPolygonLayer?.current?.setVisible(true);
				}

				// Position overlay over the image
				const topRight = getTopRight(selectedExtent);
				overlay?.current?.setPosition(topRight);

				// Start loading full resolution image in background if available
				if (fullResImageUrl) {
					// Use requestAnimationFrame to ensure compressed image is fully rendered before swapping source
					requestAnimationFrame(() => loadFullResImage(fullResImageUrl, selectedExtent));
				}
			}

			// Stop loading state
			setLoading(false);

			// Cleanup listeners regardless of success or error
			compressedImageSource.un('imageloadend', handleCompressedImageLoad);
			compressedImageSource.un('imageloaderror', handleCompressedImageLoad);
		};

		// Attach event listeners to the source
		compressedImageSource.on('imageloadend', handleCompressedImageLoad);
		compressedImageSource.on('imageloaderror', handleCompressedImageLoad);

		// Make the image layer visible
		imageLayer?.current?.setVisible(true);
	};

	const initializeLayers = useCallback(() => {
		// Create the necessary layers if they don't exist
		if (!imageLayer.current) {
			imageLayer.current = new ImageLayer({
				source: null,
				zIndex: 21,
				visible: false,
				image: {
					rotateWithView: true,
				},
			});
			mapObject.addLayer(imageLayer.current);

			console.log('added selected image layer');
		}

		if (!polygonLayer.current) {
			polygonLayer.current = new VectorLayer({
				source: new VectorSource(),
				zIndex: 24,
				visible: false,
				name: 'Detection results',
				properties: {
					customLayerId: 'polygonResultsLayer',
				},
			});
			mapObject.addLayer(polygonLayer.current);
		}

		if (!annotationPolygonLayer.current) {
			annotationPolygonLayer.current = new VectorLayer({
				source: new VectorSource(),
				zIndex: 23,
				visible: false,
				name: 'Training annotations',
				properties: {
					customLayerId: 'annotationPolygonResultsLayer',
				},
			});
			mapObject.addLayer(annotationPolygonLayer.current);
		}

		// If the select interaction is not set, set it
		if (!selectInteractionKey.current) {
			// Custom condition function
			const customCondition = mapBrowserEvent => {
				// Prevent selection if the click happened on an overlay
				if (preventClickOnOverlay(mapBrowserEvent) || interactionsDisabledRef.current)
					return false;

				// Default click condition
				return click(mapBrowserEvent);
			};

			const selectInteraction = new Select({
				layers: [singleImageLayer],
				condition: customCondition,
				style: () => null,
			});
			mapObject.addInteraction(selectInteraction);

			const interactionKey = selectInteraction.on('select', e => {
				if (e.selected.length > 0) {
					// If a feature is already selected, return
					if (selectedActive.current) return;

					// A feature was clicked, show the image
					const selectedFeature = e.selected[0];
					dispatch({
						type: 'setSelectedSingleImage',
						payload: selectedFeature,
					});
				} else {
					dispatch({ type: 'setSelectedSingleImage', payload: null });
				}
			});

			selectInteractionKey.current = interactionKey;
		}

		// Create the overlay for the tooltip
		if (!overlay.current && overlayRef.current) {
			overlay.current = createTooltip({
				mapRef: mapObject,
				tooltipRef: overlayRef.current,
				offset: [0, 0],
				positioning: 'bottom-right',
				id: 'selected-image-overlay',
			});
		}
	}, [singleImageLayer]);

	useEffect(() => {
		if (singleImageLayer) {
			initializeLayers();
		}

		return () => {
			// Reset all refs
			selectedActive.current = false;
			if (imageLayer.current) {
				mapObject.removeLayer(imageLayer.current);
				imageLayer.current = null;
			}
			if (polygonLayer.current) {
				mapObject.removeLayer(polygonLayer.current);
				polygonLayer.current = null;
			}
			if (annotationPolygonLayer.current) {
				mapObject.removeLayer(annotationPolygonLayer.current);
				annotationPolygonLayer.current = null;
			}
			if (selectInteractionKey.current) {
				unByKey(selectInteractionKey.current);
				selectInteractionKey.current = null;
			}
			if (overlay.current) {
				mapObject.removeOverlay(overlay.current);
				overlay.current = null;
				setOverlayContent(null);
			}
		};
	}, [initializeLayers]);

	useEffect(() => {
		if (!colorScheme) return;

		const polygonVectorSource = polygonLayer.current?.getSource();
		if (polygonVectorSource) {
			polygonVectorSource.getFeatures()?.forEach(feature => {
				setPolygonStyle(feature, colorScheme);
			});
		}

		const annotationVectorSource = annotationPolygonLayer.current?.getSource();
		if (annotationVectorSource) {
			annotationVectorSource.getFeatures()?.forEach(feature => {
				setAnnotationPolygonStyle(feature, colorScheme);
			});
		}
	}, [colorScheme]);

	useEffect(() => {
		updateImage(selectedSingleImage);

		return () => {
			// Cancel any ongoing image load requests
			if (imageAbortControllerRef.current) {
				imageAbortControllerRef.current.abort();
			}
			if (singleImageLayer) {
				singleImageLayer.setVisible(true);
			}
		};
	}, [selectedSingleImage]);

	// Re-render annotation polygons when annotations data changes on the selected feature
	useEffect(() => {
		if (!selectedSingleImage || !annotationPolygonLayer.current) return;

		const renderAnnotations = () => {
			const imageValues = selectedSingleImage.getProperties();
			const annotations = imageValues?.annotations;
			const polygonCoordinates = imageValues?.polygons;

			if (!polygonCoordinates) return;

			const selectedExtent = boundingExtent(polygonCoordinates);

			renderPolygonsOnLayer({
				data: annotations,
				layer: annotationPolygonLayer.current,
				extent: selectedExtent,
				styleFn: setAnnotationPolygonStyle,
				colorScheme,
			});

			annotationPolygonLayer.current.setVisible(!!annotations?.features?.length);
		};

		// Listen for annotation property changes on the selected feature
		const key = selectedSingleImage.on('change:annotations', renderAnnotations);

		// Also render immediately in case annotations already exist
		renderAnnotations();

		return () => {
			unByKey(key);
		};
	}, [selectedSingleImage, colorScheme]);

	return (
		<>
			{loading && (
				<MapLoader>
					<p className="mt-4 mb-0">
						Loading image.. <br />
						Load time depends on image size
					</p>
				</MapLoader>
			)}

			<div ref={overlayRef}>
				<SelectedImageOverlayTooltip overlayContent={overlayContent} />
			</div>
		</>
	);
};
export default SelectedSingleImageLayer;
